import { departmentOf, type NetworkDashboardAvailableStore } from '@gozoki/api-types'

export const UNKNOWN_DEPARTMENT_CODE = '__unknown__'
// Boutique synthétique « distanciel » (store_type REMOTE) : groupe dédié, toujours en bas de liste.
export const REMOTE_DEPARTMENT_CODE = '__remote__'

export interface StoreDepartment {
  code: string
  departmentCode: string | null
  label: string
  shortLabel: string
}

export interface NetworkDashboardDepartmentGroup extends StoreDepartment {
  stores: NetworkDashboardAvailableStore[]
}

interface DepartmentEntity {
  departmentCode: string | null
  isRemote?: boolean
  label: string
}

const special = (code: string, label: string): StoreDepartment => ({
  code,
  departmentCode: null,
  label,
  shortLabel: label,
})

export function storeDepartment(entity: Omit<DepartmentEntity, 'label'>): StoreDepartment {
  if (entity.isRemote) return special(REMOTE_DEPARTMENT_CODE, 'Distanciel')
  if (entity.departmentCode === null) return special(UNKNOWN_DEPARTMENT_CODE, 'Sans département')
  const code = entity.departmentCode
  const department = departmentOf(code)
  return {
    code,
    departmentCode: code,
    label: department?.label ?? code,
    shortLabel: department?.shortLabel ?? code,
  }
}

const rank = (code: string) => {
  if (code === REMOTE_DEPARTMENT_CODE) return 2
  if (code === UNKNOWN_DEPARTMENT_CODE) return 1
  return 0
}

export function groupStoresByDepartment<T extends DepartmentEntity>(
  entities: T[],
): (StoreDepartment & { stores: T[] })[] {
  const groups = new Map<string, StoreDepartment & { stores: T[] }>()
  for (const entity of entities) {
    const department = storeDepartment(entity)
    const group = groups.get(department.code) ?? { ...department, stores: [] }
    group.stores.push(entity)
    groups.set(department.code, group)
  }
  for (const group of groups.values()) group.stores.sort((a, b) => a.label.localeCompare(b.label))

  // Départements (ordre numérique) en premier, puis « Sans département » et « Distanciel ».
  return [...groups.values()].sort((a, b) => {
    const rankDiff = rank(a.code) - rank(b.code)
    if (rankDiff !== 0) return rankDiff
    return a.code.localeCompare(b.code, undefined, { numeric: true })
  })
}
